import { type ReactNode, useState } from 'react';
import { cn } from '../../utils';
import { Icon } from '../Icon';
import { Text } from '../Text';

export type CollapsibleContainerProps = {
  /**
   * The label/title text to display in the header
   */
  label: string;
  /**
   * The count to display after the label (e.g., "(5)")
   */
  count?: number;
  /**
   * Whether the container should be expanded by default
   */
  defaultExpanded?: boolean;
  /**
   * Controlled expanded state (overrides defaultExpanded if provided)
   */
  expanded?: boolean;
  /**
   * Callback fired when the expanded state changes
   */
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * The content to display when expanded
   */
  children: ReactNode;
  /**
   * Additional className for the root container
   */
  className?: string;
  /**
   * Additional className for the trigger button
   */
  triggerClassName?: string;
  /**
   * Additional className for the content container
   */
  contentClassName?: string;
};

/**
 * CollapsibleContainer provides an animated collapsible section with a header trigger
 * and animated expand/collapse functionality
 */
function CollapsibleContainer({
  label,
  count,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
  children,
  className,
  triggerClassName,
  contentClassName,
}: CollapsibleContainerProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Use controlled state if provided, otherwise use internal state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    const newExpanded = !isExpanded;
    if (controlledExpanded === undefined) {
      setInternalExpanded(newExpanded);
    }
    onExpandedChange?.(newExpanded);
  };

  return (
    <div className={cn('flex flex-col gap-0', className)}>
      <button
        type="button"
        onClick={handleToggle}
        className={cn(
          'bg-grey-20 flex items-center justify-between p-1 rounded',
          'hover:bg-grey-30 transition-colors',
          'focus-ring',
          triggerClassName,
        )}
        aria-expanded={isExpanded}
        aria-controls={`collapsible-content-${label.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <Text className="text-primary-dark text-sm font-medium">
          {label}
          {count !== undefined && ` (${count})`}
        </Text>
        <Icon
          icon="chevronDown"
          size={15}
          className={cn('transition-transform duration-300 ease-in-out', !isExpanded && 'rotate-180')}
        />
      </button>
      <div
        id={`collapsible-content-${label.replace(/\s+/g, '-').toLowerCase()}`}
        // Collapsed content stays mounted (for the animation and any child state) but is pulled from
        // focus and the accessibility tree; visibility flips at the end of the collapse transition.
        inert={!isExpanded}
        className={cn(
          'grid transition-[grid-template-rows,visibility] duration-300 ease-in-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr] invisible',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className={cn('flex flex-col gap-0', contentClassName)}>{children}</div>
        </div>
      </div>
    </div>
  );
}

export { CollapsibleContainer };
